// ─── Battle effects — shared primitives ──────────────────────────────────────
//
// The reveal's animated pieces, board-agnostic: each is positioned by the
// percentages its parent layer hands it, so the square board (BoardGrid, its
// column overlays) and the hex board (hex/battle.js, its corridor overlays)
// drive the same components from their own geometry. Styles live in
// BoardGrid.css (both boards import it). Copied verbatim from BoardGrid's
// module-private versions so the legacy board keeps working untouched until
// it is retired.

import { COLOR } from '../../config/uiTokens'
import { WaspFlap } from './MissileIcon'

// The circle-of-fire reveal sweep pace: each fresh crater ignites as the
// expanding ring passes over it, delayed by its distance from the aim at this
// per-cell pace, so the two stay in lockstep.
export const FIRE_SWEEP_MS_PER_CELL = 90

// The kill silhouette rides the same sweep clock: a killed structure waits for
// the fire front to finish its footprint and its blast clouds to rise before
// its silhouette leaves the board for the kill bar.
export const MOTE_STRUCT_LAG_MS = 800

// A unit tile's firing recoil (must match the bfc-fire-shake keyframes).
export const MISSILE_SHAKE_MS = 320

// Which side is the aggressor (and so fires first each exchange). The clash
// context names who attacked; mutual attacks fall back to the prevailing
// (winning) side, and an unknown context defaults to self.
export function attackerSideOf(context, winner) {
    if (context === 'opp_attacked')  return 'opp'
    if (context === 'self_attacked') return 'self'
    return winner === 'opp' ? 'opp' : 'self'
}

// ─── Single missile ───────────────────────────────────────────────────────────
// One army's missile, drawn as three stacked layers that share the same flight
// timeline (animation-delay = launchDelay, duration = travelDuration):
//   • the anchor (.bg__missile) glides along the path — top: --mi-start → --mi-end —
//     and fades in/out (bg__missile-fly). animation-fill-mode: both keeps it hidden
//     at its start row until launch, so a not-yet-fired shot never sits on its unit.
//   • the craft (the SVG triangle + exhaust trail) grows as it gains altitude, faking
//     a Z-axis climb. flightKind 'rise' grows to a peak at the end (intercepted in the
//     air); 'land' arcs — grows then shrinks back down onto its ground target.
//   • the shadow stays pinned to the flight path and drifts beneath the craft, so the
//     widening gap reads as lift-off.
// The shot in flight: the flapping wasp (MissileIcon WaspFlap), head along
// its flight — yours black with an amber outline (amber running to red, the
// attacking tiles' gradient, when you're the attacker in that clash), theirs
// red with a white one, pointing down the board. SHOT_W: its width in px (centred on
// the 18 × 32 shot box, overflowing it).
const SHOT_W = 30
const SHOT_PAINT = {
    self: { fill: '#000000', outline: COLOR.GRAD_AMBER_LIGHT },
    selfAttacking: { fill: '#000000', outlineGradient: [['0', COLOR.GRAD_AMBER_LIGHT], ['1', COLOR.GRAD_RED]] },
    opp:  { fill: '#e05050', outline: '#ffffff' },
}
export function SingleMissile({ side, startPct, endPct, leftStartPct = 50, leftEndPct = 50, launchDelay, travelDuration, flightKind = 'rise', attacking = false }) {
    const anim = { animationDelay: `${launchDelay}ms`, animationDuration: `${travelDuration}ms` }

    return (
        <div
            className={`bg__missile${flightKind === 'land' ? ' bg__missile--land' : ''}`}
            style={{ '--mi-start': `${startPct}%`, '--mi-end': `${endPct}%`,
                     '--mi-left-start': `${leftStartPct}%`, '--mi-left-end': `${leftEndPct}%`,
                     ...anim }}
        >
            <span className="bg__missile-shadow" style={anim} />
            <span className={`bg__missile-craft bg__missile-craft--${flightKind}`} style={anim}>
                <span className={`bg__missile-trail bg__missile-trail--${side}`} />
                <svg className="bg__missile-wasp" viewBox="0 0 120 108" width={SHOT_W} height={SHOT_W * 0.9}
                    style={{ transform: `translate(-50%, -50%)${side === 'opp' ? ' rotate(180deg)' : ''}` }} overflow="visible">
                    <WaspFlap {...SHOT_PAINT[side === 'self' && attacking ? 'selfAttacking' : side]} />
                </svg>
            </span>
        </div>
    )
}

// ─── Explosion ────────────────────────────────────────────────────────────────
// Radial burst at the cancellation point (divider) or hit point (target). dx/dy
// nudge the burst off-centre so multiple hits on one cell read as distinct
// flashes (one per surplus missile) instead of stacking into a single point.
export function Explosion({ top, leftPct = 50, delay, variant, dx = 0, dy = 0 }) {
    return (
        <div
            className={`bg__explosion bg__explosion--${variant}`}
            style={{ top: `${top}%`, left: `${leftPct}%`, marginLeft: `${dx}px`, marginTop: `${dy}px`, animationDelay: `${delay}ms` }}
        />
    )
}

// Deterministic scatter for successive hits on the same cell — keeps each
// surplus missile's burst visibly separate (and countable) without Math.random.
export const HIT_SCATTER = [[0, -1], [-4, 2], [4, -2], [-2, -3], [4, 3], [-5, 0], [2, 4], [-1, -4]]

// ─── Smoke plume ──────────────────────────────────────────────────────────────
// Fired when two missiles destroy each other at the divider: a quick bright
// detonation flash, then several grey puffs that billow outward, drift up and
// dissipate. Puff configs are fixed (no Math.random) so they stay stable across
// re-renders; each puff drifts to (--sm-dx, --sm-dy) and grows by --sm-scale.
const SMOKE_PUFFS = [
    { dx:   0, dy: -13, scale: 2.4, dur: 720, d:   0 },
    { dx: -13, dy:  -6, scale: 2.0, dur: 640, d:  40 },
    { dx:  14, dy:  -8, scale: 2.0, dur: 680, d:  55 },
    { dx:  -8, dy: -19, scale: 1.6, dur: 780, d: 100 },
    { dx:   9, dy: -22, scale: 1.5, dur: 760, d: 120 },
]
export function SmokePlume({ top, leftPct = 50, delay }) {
    return (
        <div className="bg__smoke" style={{ top: `${top}%`, left: `${leftPct}%` }}>
            <span className="bg__smoke-flash" style={{ animationDelay: `${delay}ms` }} />
            {SMOKE_PUFFS.map((p, i) => (
                <span
                    key={i}
                    className="bg__smoke-puff"
                    style={{
                        '--sm-dx': `${p.dx}px`,
                        '--sm-dy': `${p.dy}px`,
                        '--sm-scale': p.scale,
                        animationDelay:    `${delay + p.d}ms`,
                        animationDuration: `${p.dur}ms`,
                    }}
                />
            ))}
        </div>
    )
}

// ─── Wash label ───────────────────────────────────────────────────────────────
// One label, centred on the clash corridor at the band (leftPct: where the
// corridor's middle falls across its strip).
export function WashLabel({ leftPct = 50 }) {
    return <div className="bg__wash-label" style={{ left: `${leftPct}%` }} data-wash="true">Wash</div>
}

// ─── Rocket interceptor ─────────────────────────────────────────────────────
// A defender's rocket: blue, smaller and stouter than a missile, flying a
// wiggling/spiralling path from the defended cell to the interception point,
// where it meets and cancels an incoming shot.
export function RocketInterceptor({ startPct, endPct, leftPct = 50, launchDelay, travelDuration, dir }) {
    return (
        <div
            className="bg__rocket-intercept"
            style={{
                left: `${leftPct}%`,
                '--rk-start': `${startPct}%`,
                '--rk-end':   `${endPct}%`,
                animationDelay:    `${launchDelay}ms`,
                animationDuration: `${travelDuration}ms`,
            }}
        >
            <span className="bg__rocket-intercept-inner" style={{ transform: dir === 'down' ? 'rotate(180deg)' : 'none' }}>
                <svg viewBox="0 0 16 36" width="14" height="32">
                    <polygon points="8,1.5 2,34 14,34"
                             fill="#3da5ff" stroke="rgba(255,255,255,0.95)" strokeWidth="2.2" strokeLinejoin="round" />
                    <circle cx="8" cy="30.5" r="2.2" fill="#cfe9ff" />
                </svg>
            </span>
        </div>
    )
}

// The meeting point of an intercepted shot, as a fraction of the way from the
// defended cell back toward the divider.
export const INTERCEPT_FRACTION = 0.5

// ─── Recon plane glyph ────────────────────────────────────────────────────────
// The surveillance plane silhouette (mirrors public/recon_plane_01.svg): a
// fuselage with a nose taper, a long straight wing and a short tailplane.
export function ReconPlanePaths({ fill, stroke = 'none', strokeWidth = 0 }) {
    return (
        <g fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round">
            <path d="M32 1.5 C34.6 1.5 36.2 5.5 36.2 12 L36.2 25 L35.6 30 L35.6 36 L36 42 L36 50 L35.4 56.5 C35 60.5 33.9 62.5 32 62.5 C30.1 62.5 29 60.5 28.6 56.5 L28 50 L28 42 L28.4 36 L28.4 30 L27.8 25 L27.8 12 C27.8 5.5 29.4 1.5 32 1.5 Z" />
            <path d="M3.5 27.2 L27.8 26.2 L36.2 26.2 L60.5 27.2 C62.2 27.3 63 28 63 29.2 L63 30.2 C63 31.4 62.2 32.1 60.5 32.2 L36.2 33.6 L27.8 33.6 L3.5 32.2 C1.8 32.1 1 31.4 1 30.2 L1 29.2 C1 28 1.8 27.3 3.5 27.2 Z" />
            <path d="M23 52.4 L41 52.4 C42.4 52.4 43.1 53 43.1 54.2 L43.1 54.8 C43.1 56 42.4 56.6 41 56.6 L23 56.6 C21.6 56.6 20.9 56 20.9 54.8 L20.9 54.2 C20.9 53 21.6 52.4 23 52.4 Z" />
        </g>
    )
}
export function DroneFlightGlyph({ fill, stroke }) {
    return (
        <svg className="bg__drone-svg" viewBox="0 0 64 64" aria-hidden="true">
            <ReconPlanePaths fill={stroke} stroke={stroke} strokeWidth={7} />
            <ReconPlanePaths fill={fill} />
        </svg>
    )
}
// Your plane wears the surveillance purple (#a855f7, matching the buy tab) in
// a black stroke; the opponent's is black in a red one (their crates' scheme).
export const DRONE_FLIGHT_COLORS = {
    self: { fill: '#a855f7', stroke: '#000000' },
    opp:  { fill: '#000000', stroke: '#e05050' },
}
